import React, { useEffect, useState } from 'react';
import {
  Receipt,
  Plus,
  Download,
  Trash2,
  Loader2,
  CheckCircle,
  X,
  Send,
  Mail,
  Link as LinkIcon,
} from 'lucide-react';
import { invoiceAPI, customerAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const STATUSES = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Draft', class: 'bg-gray-500/20 text-gray-300' },
  { id: 'sent', label: 'Sent', class: 'bg-blue-500/20 text-blue-400' },
  { id: 'paid', label: 'Paid', class: 'bg-emerald-500/20 text-emerald-400' },
  { id: 'overdue', label: 'Overdue', class: 'bg-rose-500/20 text-rose-400' },
  { id: 'void', label: 'Void', class: 'bg-slate-500/20 text-slate-400' },
];

const emptyForm = {
  customer_id: '',
  title: '',
  description: '',
  line_items: [{ description: '', quantity: 1, unit_price: 0 }],
  tax_rate_percent: 0,
  due_in_days: 30,
  notes: '',
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    load();
  }, []);
  useEffect(() => {
    if (statusFilter === 'all') setFiltered(invoices);
    else setFiltered(invoices.filter((i) => i.status === statusFilter));
  }, [invoices, statusFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const [i, c] = await Promise.all([invoiceAPI.list(), customerAPI.list()]);
      setInvoices(i || []);
      setCustomers(c || []);
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (a) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(a || 0);
  const custName = (id) => {
    const c = customers.find((c) => c.id === id);
    return c ? `${c.first_name} ${c.last_name}` : 'Unknown';
  };

  const subtotal = form.line_items.reduce(
    (s, li) => s + (Number(li.quantity || 0) * Number(li.unit_price || 0)),
    0,
  );
  const tax = subtotal * (Number(form.tax_rate_percent || 0) / 100);
  const total = subtotal + tax;

  const updateLi = (idx, k, v) => {
    const items = [...form.line_items];
    items[idx] = { ...items[idx], [k]: k === 'description' ? v : Number(v) };
    setForm({ ...form, line_items: items });
  };
  const addLi = () =>
    setForm({
      ...form,
      line_items: [...form.line_items, { description: '', quantity: 1, unit_price: 0 }],
    });
  const removeLi = (idx) =>
    setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.title) {
      toast.error('Customer and title required');
      return;
    }
    setSubmitting(true);
    try {
      await invoiceAPI.create(form);
      toast.success('Invoice created');
      setDialogOpen(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error('Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  const changeStatus = async (id, status, extra = {}) => {
    try {
      await invoiceAPI.update(id, { status, ...extra });
      toast.success(`Invoice ${status}`);
      load();
    } catch (e) {
      toast.error('Update failed');
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const blob = await invoiceAPI.downloadPdf(inv.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoice_number || inv.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not download PDF');
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    try {
      await invoiceAPI.delete(inv.id);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const emailInvoice = async (inv) => {
    try {
      const result = await invoiceAPI.email(inv.id);
      toast.success(`Email sent to ${result.to}`);
      load();
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || 'Failed to send email';
      if (status === 503) {
        toast.error('Email is not configured. Add RESEND_API_KEY to backend .env to enable sending.');
      } else {
        toast.error(msg);
      }
    }
  };

  const copyPayLink = async (inv) => {
    try {
      const result = await invoiceAPI.payLink(inv.id);
      // Always build the URL from the browser's origin so the copied link uses
      // the customer-facing public domain, not the internal cluster hostname
      // that the backend may have synthesised from request.base_url.
      const url = `${window.location.origin}/pay/i/${result.public_token}`;
      await navigator.clipboard.writeText(url);
      toast.success('Pay link copied to clipboard');
    } catch (e) {
      toast.error('Could not generate pay link');
    }
  };

  const totalPaid = invoices.filter((i) => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);
  const totalUnpaid = invoices
    .filter((i) => ['draft', 'sent', 'overdue'].includes(i.status))
    .reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="space-y-6 fade-in" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <Receipt className="w-8 h-8 text-emerald-400" /> Invoices
          </h1>
          <p className="text-muted-foreground">Bill customers, mark paid, and download PDFs.</p>
        </div>
        <Button
          onClick={() => setDialogOpen(true)}
          className="btn-gradient-primary"
          data-testid="add-invoice-button"
        >
          <Plus className="w-4 h-4 mr-2" /> New Invoice
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Total Paid" value={fmt(totalPaid)} accent="text-emerald-400" />
        <Stat label="Outstanding" value={fmt(totalUnpaid)} accent="text-amber-400" />
        <Stat label="Count" value={invoices.length} accent="text-sky-400" />
      </div>

      <Tabs value={statusFilter} onValueChange={setStatusFilter}>
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar">
          {STATUSES.map((s) => (
            <TabsTrigger key={s.id} value={s.id} data-testid={`invoice-filter-${s.id}`}>
              {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {loading ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Receipt className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No invoices yet</h3>
            <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary">
              <Plus className="w-4 h-4 mr-2" /> Create First Invoice
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((inv) => {
            const sc = STATUSES.find((s) => s.id === inv.status);
            return (
              <Card key={inv.id} className="card-hover" data-testid={`invoice-${inv.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold">{inv.title}</h3>
                        <Badge className={`${sc?.class} border-0`}>{inv.status}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {inv.invoice_number} • {custName(inv.customer_id)}
                        {inv.due_at ? ` • Due ${inv.due_at.slice(0, 10)}` : ''}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-emerald-400">{fmt(inv.total)}</p>
                      <div className="flex gap-2 mt-2 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(inv)}
                          data-testid={`invoice-${inv.id}-pdf-button`}
                        >
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => emailInvoice(inv)}
                          data-testid={`invoice-${inv.id}-email-button`}
                        >
                          <Mail className="w-3 h-3 mr-1" /> Email
                        </Button>
                        {inv.status !== 'paid' && inv.status !== 'void' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyPayLink(inv)}
                            data-testid={`invoice-${inv.id}-paylink-button`}
                            title="Copy a public Pay Now link"
                          >
                            <LinkIcon className="w-3 h-3 mr-1" /> Pay Link
                          </Button>
                        )}
                        {inv.status === 'draft' && (
                          <Button
                            size="sm"
                            onClick={() => changeStatus(inv.id, 'sent')}
                            className="btn-gradient-info"
                          >
                            <Send className="w-3 h-3 mr-1" /> Send
                          </Button>
                        )}
                        {inv.status === 'sent' && (
                          <Button
                            size="sm"
                            onClick={() => changeStatus(inv.id, 'paid', { paid_at: new Date().toISOString() })}
                            className="btn-gradient-success"
                            data-testid={`invoice-${inv.id}-mark-paid-button`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => remove(inv)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
            <DialogDescription>Bill a customer for services rendered</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Customer *</Label>
                <Select
                  value={form.customer_id}
                  onValueChange={(v) => setForm({ ...form, customer_id: v })}
                >
                  <SelectTrigger data-testid="invoice-customer-select">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.first_name} {c.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Invoice title"
                  required
                  data-testid="invoice-title-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Line Items</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLi}>
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {form.line_items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                    <Input
                      className="col-span-6"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateLi(idx, 'description', e.target.value)}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      min="0"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateLi(idx, 'quantity', e.target.value)}
                    />
                    <Input
                      className="col-span-2"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="Price"
                      value={item.unit_price}
                      onChange={(e) => updateLi(idx, 'unit_price', e.target.value)}
                    />
                    <div className="col-span-1 text-sm font-semibold">
                      {fmt(Number(item.quantity || 0) * Number(item.unit_price || 0))}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="col-span-1"
                      onClick={() => removeLi(idx)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.tax_rate_percent}
                  onChange={(e) =>
                    setForm({ ...form, tax_rate_percent: Number(e.target.value) })
                  }
                  data-testid="invoice-tax-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Due In (days)</Label>
                <Input
                  type="number"
                  min="1"
                  value={form.due_in_days}
                  onChange={(e) =>
                    setForm({ ...form, due_in_days: Number(e.target.value) })
                  }
                />
              </div>
            </div>

            <div className="p-4 bg-muted rounded-lg flex items-center justify-between flex-col sm:flex-row gap-2">
              <div className="space-y-1 text-sm">
                <div className="flex gap-4">
                  <span>Subtotal: {fmt(subtotal)}</span>
                  <span>
                    Tax ({form.tax_rate_percent || 0}%): {fmt(tax)}
                  </span>
                </div>
              </div>
              <span className="text-3xl font-bold text-emerald-400" data-testid="invoice-total">
                {fmt(total)}
              </span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn-gradient-primary"
                disabled={submitting}
                data-testid="invoice-submit-button"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Invoice'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <Card>
    <CardContent className="p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
    </CardContent>
  </Card>
);
